import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Notification center — a bell in the navbar with an unread badge that
// opens a dropdown of the user's notifications in a single chronological
// (newest-first) list, with "mark all read". Notification content is
// generated server-side on app load; this component just displays and
// acknowledges it. The per-type icon still distinguishes kinds at a glance.
import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
function iconOf(type) {
    switch (type) {
        case "nudge":
            return "👋";
        case "block_start_reminder":
            return "🕐";
        case "morning_planning_nudge":
            return "🌅";
        case "task_assigned":
            return "👤";
        case "task_due_soon":
            return "📅";
        case "end_of_day_review":
            return "🌙";
        default:
            return "🔔";
    }
}
// Pull the free-text note out of a nudge notification's payload — shown
// under the headline so the recipient sees what was actually said.
function nudgeNote(n) {
    if (n.type !== "nudge" || !n.payload)
        return null;
    const note = n.payload.note;
    return typeof note === "string" && note.trim() ? note : null;
}
function fmtRelative(iso) {
    const then = new Date(iso).getTime();
    const diffMin = Math.round((Date.now() - then) / 60000);
    if (diffMin < 1)
        return "just now";
    if (diffMin < 60)
        return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24)
        return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 7)
        return `${diffDay}d ago`;
    return new Date(iso).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
    });
}
export default function NotificationCenter() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const rootRef = useRef(null);
    async function load() {
        setLoading(true);
        try {
            setItems(await api.get("/notifications"));
        }
        catch {
            /* leave whatever we had */
        }
        finally {
            setLoading(false);
        }
    }
    // Initial load for the badge + refresh when the tab regains focus.
    useEffect(() => {
        load();
        const onFocus = () => void load();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, []);
    // Close on outside click / Escape.
    useEffect(() => {
        if (!open)
            return;
        function onDoc(e) {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        function onKey(e) {
            if (e.key === "Escape")
                setOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);
    function toggleOpen() {
        const next = !open;
        setOpen(next);
        if (next)
            void load();
    }
    async function markAllRead() {
        const now = new Date().toISOString();
        setItems((xs) => xs.map((n) => (n.read_at ? n : { ...n, read_at: now })));
        try {
            await api.post("/notifications/read-all");
        }
        catch {
            void load();
        }
    }
    async function markOneRead(n) {
        if (n.read_at)
            return;
        const now = new Date().toISOString();
        setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, read_at: now } : x)));
        try {
            await api.post(`/notifications/${n.id}/read`);
        }
        catch {
            void load();
        }
    }
    const unread = items.filter((n) => !n.read_at).length;
    // Single chronological list, newest first — no category grouping. We sort
    // client-side rather than trusting the API's order so the list reads
    // strictly by time regardless of how the feed is assembled.
    const sorted = [...items].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return (_jsxs("div", { className: "notif-center", ref: rootRef, children: [_jsxs("button", { type: "button", className: "notif-bell", "aria-label": unread > 0
                    ? `Notifications, ${unread} unread`
                    : "Notifications", onClick: toggleOpen, children: [_jsx("span", { "aria-hidden": "true", children: "\uD83D\uDD14" }), unread > 0 && (_jsx("span", { className: "notif-badge", children: unread > 9 ? "9+" : unread }))] }), open && (_jsxs("div", { className: "notif-panel", role: "dialog", "aria-label": "Notifications", children: [_jsxs("div", { className: "notif-panel-head", children: [_jsx("strong", { children: "Notifications" }), unread > 0 && (_jsx("button", { type: "button", className: "btn-link", onClick: markAllRead, children: "Mark all read" }))] }), loading && items.length === 0 && (_jsx("div", { className: "notif-empty muted small", children: "Loading\u2026" })), !loading && items.length === 0 && (_jsx("div", { className: "notif-empty muted small", children: "You're all caught up." })), _jsx("div", { className: "notif-scroll", children: sorted.map((n) => (_jsxs("button", { type: "button", className: `notif-row${n.read_at ? "" : " is-unread"}`, onClick: () => markOneRead(n), children: [_jsx("span", { className: "notif-row-icon", "aria-hidden": "true", children: iconOf(n.type) }), _jsxs("span", { className: "notif-row-body", children: [_jsx("span", { className: "notif-row-msg", children: n.message }), nudgeNote(n) && (_jsxs("span", { className: "notif-row-note", children: ["\"", nudgeNote(n), "\""] })), _jsx("span", { className: "notif-row-time muted small", children: fmtRelative(n.created_at) })] }), !n.read_at && (_jsx("span", { className: "notif-row-dot", "aria-hidden": "true" }))] }, n.id))) })] }))] }));
}
